import { useCallback } from 'react'
import analytics from '@/lib/analytics'
import { MIXPANEL_EVENTS, MIXPANEL_PROPERTIES } from '@/constants/mixpanel'

export const useTracking = () => {
  // Use the analytics service's enabled state
  const isTrackingEnabled = analytics.isEnabled

  const track = useCallback(
    (event: string, properties?: Record<string, any>) => {
      if (!isTrackingEnabled) return
      analytics.track(event, properties)
    },
    [isTrackingEnabled],
  )

  const trackAnalysis = useCallback(
    (fileType: string) => {
      if (!isTrackingEnabled) return
      track(MIXPANEL_EVENTS.RUN_ANALYSIS, {
        [MIXPANEL_PROPERTIES.FILE_TYPE]: fileType,
      })
    },
    [track, isTrackingEnabled],
  )

  const trackPageView = useCallback(
    (pagePath: string, pageSearch?: string, pageHash?: string) => {
      if (!isTrackingEnabled) return
      track(MIXPANEL_EVENTS.PAGE_VIEW, {
        [MIXPANEL_PROPERTIES.PAGE_PATH]: pagePath,
        [MIXPANEL_PROPERTIES.PAGE_SEARCH]: pageSearch || '',
        [MIXPANEL_PROPERTIES.PAGE_HASH]: pageHash || '',
        [MIXPANEL_PROPERTIES.FULL_PATH]:
          pagePath + (pageSearch || '') + (pageHash || ''),
        [MIXPANEL_PROPERTIES.PAGE_TITLE]: document.title,
      })
    },
    [track, isTrackingEnabled],
  )

  const trackFrameAnalysis = useCallback(() => {
    if (!isTrackingEnabled) return
    track(MIXPANEL_EVENTS.VIEW_FRAME_ANALYSIS)
  }, [track, isTrackingEnabled])

  const trackPrintResults = useCallback(() => {
    if (!isTrackingEnabled) return
    track(MIXPANEL_EVENTS.PRINT_ANALYSIS_RESULTS)
  }, [track, isTrackingEnabled])

  const trackCompareAnalyses = useCallback(
    (analysisIds: string[]) => {
      if (!isTrackingEnabled) return
      track(MIXPANEL_EVENTS.COMPARE_ANALYSES_CLICKED, {
        analysis_ids: analysisIds,
      })
    },
    [track, isTrackingEnabled],
  )

  const trackEnhancementSuggestions = useCallback(
    (analysisId: string, conversationId: string) => {
      if (!isTrackingEnabled) return
      track(MIXPANEL_EVENTS.ENHANCEMENT_SUGGESTIONS, {
        analysis_id: analysisId,
        conversation_id: conversationId,
      })
    },
    [track, isTrackingEnabled],
  )

  const trackAuth = useCallback(
    (
      event: 'signed_in' | 'signed_out',
      userProperties?: Record<string, any>,
    ) => {
      if (!isTrackingEnabled) return
      track(event, userProperties)
    },
    [track, isTrackingEnabled],
  )

  return {
    track,
    trackAnalysis,
    trackPageView,
    trackFrameAnalysis,
    trackPrintResults,
    trackCompareAnalyses,
    trackAuth,
    trackEnhancementSuggestions,
  }
}
